import { api } from './apiClient';

/** What a user can DO — the gate vocabulary (mirrors the backend). */
export type Capability = 'media' | 'sports' | 'lobbies' | 'downloads';

export type BillingKind = 'admin' | 'comp' | 'owned' | 'subscribed' | 'limited' | 'none';

/** One-time ownable tiers. tier3 is recurring and not "owned". */
export type OwnableTier = 'tier1' | 'tier2';

export interface DailyMedia {
  limit: number;
  used: number;
  remaining: number;
  /** True once the daily free allowance is spent — show a pre-roll ad. */
  adRequired: boolean;
}

export interface SubscriptionSummary {
  status: string;
  priceCents: number;
  currentPeriodEnd: string | null;
}

export interface BillingStatus {
  kind: BillingKind;
  capabilities: Capability[];
  ownedTier: OwnableTier | null;
  tier3Active: boolean;
  /** Non-null only for limited (recruitee) users. */
  dailyMedia: DailyMedia | null;
  subscription: SubscriptionSummary | null;
  isRecruiter: boolean;
  paywallEnabled: boolean;
  /** null = unlimited. Present once RECRUITER_PLANS_ENABLED is on. */
  screenLimit?: number | null;
  recruiterPlansEnabled?: boolean;
}

export type TierKey = 'tier1' | 'tier2' | 'tier3';

export interface PlanTier {
  key: TierKey;
  label: string;
  priceCents: number;
  oneTime: boolean;
  capabilities: Capability[];
  requiresBase?: boolean;
}

export interface Plans {
  tiers: PlanTier[];
  freeStreamDailyLimit: number;
  commissionRate: number;
  withdrawMinCents: number;
  checkoutEnabled: boolean;
}

export interface ReferralSummary {
  isRecruiter: boolean;
  referralCode: string | null;
  recruitedCount: number;
  earnedCents: number;
  withdrawnCents: number;
  balanceCents: number;
  canWithdraw: boolean;
  withdrawMinCents: number;
  commissionRate: number;
  payoutsEnabled: boolean;
}

export const billingApi = {
  getMe(): Promise<BillingStatus> {
    return api<BillingStatus>('/api/billing/me', { auth: true });
  },
  getPlans(): Promise<Plans> {
    return api<Plans>('/api/billing/plans');
  },
  /** Start checkout for a single tier (one-time for tier1/2, recurring for tier3). */
  createCheckout(tier: TierKey): Promise<{ url: string }> {
    return api<{ url: string }>('/api/billing/checkout', { method: 'POST', body: { tier }, auth: true });
  },
  /** Success-page fallback: confirm a completed Checkout Session, granting access
   *  immediately if the webhook is delayed. Returns the refreshed status. */
  confirmCheckout(sessionId: string): Promise<BillingStatus> {
    return api<BillingStatus>('/api/billing/checkout/confirm', { method: 'POST', body: { sessionId }, auth: true });
  },
  getReferral(): Promise<ReferralSummary> {
    return api<ReferralSummary>('/api/billing/referral', { auth: true });
  },
  mintReferralCode(): Promise<{ code: string }> {
    return api<{ code: string }>('/api/billing/referral/code', { method: 'POST', auth: true });
  },
  requestWithdrawal(): Promise<{ id: string; amountCents: number; status: string }> {
    return api<{ id: string; amountCents: number; status: string }>('/api/billing/withdraw', { method: 'POST', auth: true });
  },
  startConnectOnboard(): Promise<{ url: string }> {
    return api<{ url: string }>('/api/billing/connect/onboard', { method: 'POST', auth: true });
  },
};

/** Format integer cents as a USD string, e.g. 250 -> "$2.50". */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
